import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Copy, ExternalLink, QrCode, Download, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../lib/api";

const money = (v) => (v || v === 0 ? `$${Number(v).toLocaleString()}` : "—");

const CLASS_STYLES = {
  "Strong Match": "bg-emerald-500/10 text-emerald-600 border-emerald-500/30",
  "Potential Match": "bg-sky-500/10 text-sky-600 border-sky-500/30",
  "Weak Match": "bg-amber-500/10 text-amber-600 border-amber-500/30",
  "Does Not Match": "bg-rose-500/10 text-rose-600 border-rose-500/30",
};

function AngelaBadge({ angela }) {
  if (!angela || angela.status === "scoring") {
    return <span className="inline-flex items-center gap-1 text-xs text-muted-foreground"><Loader2 className="h-3 w-3 animate-spin" /> Scoring…</span>;
  }
  const cls = CLASS_STYLES[angela.classification] || "bg-muted text-muted-foreground border-border";
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-full border ${cls}`} title={angela.summary || ""}>
      {typeof angela.score === "number" ? `${angela.score} · ` : ""}{angela.classification || "—"}
    </span>
  );
}

export default function SellYourLandAdmin() {
  const navigate = useNavigate();
  const [qr, setQr] = useState(null);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get(`/sell-land/qr`, { params: { base: origin } }).then((r) => setQr(r.data)).catch(() => {}),
      api.get(`/sell-land/submissions`).then((r) => setSubs(r.data.items || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => {
    document.title = "ARQITEK — Sell Your Land";
    load();
    api.post(`/sell-land/submissions/mark-viewed`).catch(() => {});
    const t = setInterval(() => {
      api.get(`/sell-land/submissions`).then((r) => setSubs(r.data.items || [])).catch(() => {});
    }, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const copyLink = () => {
    navigator.clipboard.writeText(qr?.url || `${origin}/sell-your-land`);
    toast.success("Public link copied");
  };

  const downloadQr = () => {
    if (!qr?.qr) return;
    const a = document.createElement("a");
    a.href = qr.qr;
    a.download = "arqitek-sell-your-land-qr.png";
    a.click();
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-8" data-testid="sell-land-admin">
      <div>
        <span className="block text-xs uppercase tracking-[0.15em] text-primary mb-2">Operations</span>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Sell Your Land</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          A public portal where landowners tell you what they'd sell for. Share the link or print the QR code
          on your mailers and signs — submissions land in your Leads pipeline and are auto-scored by Angela.
        </p>
      </div>

      <div className="space-y-6">
        {/* Submissions */}
        <div className="rounded-xl border border-border bg-card p-5" data-testid="sell-land-submissions-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold">Seller submissions <span className="text-muted-foreground font-normal">({subs.length})</span></h3>
            <Button variant="ghost" size="sm" onClick={load} data-testid="sell-land-refresh-btn"><RefreshCw className="h-3.5 w-3.5 mr-1.5" />Refresh</Button>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : subs.length === 0 ? (
            <div className="text-sm text-muted-foreground py-16 text-center" data-testid="sell-land-empty">
              No submissions yet. Share your link or QR code to start collecting seller offers.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="py-2 pr-3 font-medium">Seller</th>
                    <th className="py-2 pr-3 font-medium">Property</th>
                    <th className="py-2 pr-3 font-medium">Asking</th>
                    <th className="py-2 pr-3 font-medium">Source</th>
                    <th className="py-2 pr-3 font-medium">Date</th>
                    <th className="py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map((s) => (
                    <tr key={s.id} className="border-b border-border/60 hover:bg-accent/40" data-testid="sell-land-sub-row">
                      <td className="py-2.5 pr-3">
                        <div className="font-medium">{s.seller_name || "—"}</div>
                        <div className="text-xs text-muted-foreground">{s.seller_email || s.seller_phone || ""}</div>
                      </td>
                      <td className="py-2.5 pr-3">
                        <div>{s.address || s.apn || "—"}</div>
                        <div className="text-xs text-muted-foreground">{[s.city, s.state].filter(Boolean).join(", ")}</div>
                      </td>
                      <td className="py-2.5 pr-3 font-medium">{money(s.asking_price)}</td>
                      <td className="py-2.5 pr-3">
                        {s.matched
                          ? <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/30">From mailer</span>
                          : <span className="text-xs text-muted-foreground">QR / link</span>}
                      </td>
                      <td className="py-2.5 pr-3 text-xs text-muted-foreground">{s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}</td>
                      <td className="py-2.5">
                        <Button variant="ghost" size="sm" data-testid="sell-land-open-record"
                          onClick={() => navigate(`/record/${s.record_resource}/${s.record_id}`)}>
                          Open <ExternalLink className="h-3.5 w-3.5 ml-1" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* QR / link */}
        <div className="rounded-xl border border-border bg-card p-5" data-testid="sell-land-qr-card">
          <div className="flex items-center gap-2 mb-4">
            <QrCode className="h-4 w-4 text-primary" />
            <h3 className="text-sm font-semibold">Your public QR &amp; link</h3>
          </div>
          {qr?.qr ? (
            <img src={qr.qr} alt="Sell Your Land QR" className="w-44 h-44 rounded-lg border border-border bg-white p-2" data-testid="sell-land-qr-img" />
          ) : (
            <div className="w-44 h-44 rounded-lg border border-border flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          )}
          <div className="mt-4 text-xs text-muted-foreground break-all" data-testid="sell-land-public-url">{qr?.url || `${origin}/sell-your-land`}</div>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button variant="outline" size="sm" onClick={copyLink} data-testid="sell-land-copy-btn"><Copy className="h-3.5 w-3.5 mr-1.5" />Copy link</Button>
            <Button variant="outline" size="sm" onClick={downloadQr} data-testid="sell-land-download-btn"><Download className="h-3.5 w-3.5 mr-1.5" />QR PNG</Button>
            <Button variant="outline" size="sm" onClick={() => window.open(qr?.url || `${origin}/sell-your-land`, "_blank")}><ExternalLink className="h-3.5 w-3.5 mr-1.5" />Open</Button>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">Tip: each mailer you send also carries its own QR that links the response straight to that lead.</p>
        </div>
      </div>
    </div>
  );
}
